import * as THREE from 'three';
import { Transaction } from '../../core/models/Transaction';
import { MempoolStats } from '../effects/MempoolNebula';

export interface TransactionParticle {
  id: string;
  position: THREE.Vector3;
  velocity: THREE.Vector3;
  lifetime: number;
  maxLifetime: number;
  feeRate: number;
  size: number;
  color: THREE.Color;
  targetShip?: string;
  age: number;
}

export interface NebulaParticlesConfig {
  maxParticles: number;
  bounds: {
    center: THREE.Vector3;
    innerRadius: number;
    outerRadius: number;
    height: number;
  };
  qualityLevel: 'low' | 'medium' | 'high' | 'ultra';
}

export interface ParticleBehavior {
  swirl: number;
  drift: number;
  turbulence: number;
  attraction: number;
}

export class NebulaParticles extends THREE.Group {
  private config: NebulaParticlesConfig;
  
  // Particle pools
  private maxParticles: number;
  private activeParticles: TransactionParticle[] = [];
  private particleGeometry: THREE.BufferGeometry;
  private particleMaterial: THREE.ShaderMaterial;
  private particleSystem: THREE.Points;
  
  // Spawn parameters
  private spawnRate: number = 5; // particles per second
  private spawnDistribution: 'uniform' | 'fee-weighted' = 'fee-weighted';
  
  // Behavior parameters
  private behavior: ParticleBehavior = {
    swirl: 0.5,
    drift: 0.3,
    turbulence: 0.2,
    attraction: 0.1,
  };
  
  // Buffer arrays for GPU
  private positions: Float32Array;
  private colors: Float32Array;
  private sizes: Float32Array;
  private lifetimes: Float32Array;
  private feeRates: Float32Array;
  
  // Animation
  private time: number = 0;
  private lastSpawn: number = 0;
  
  // Mempool stats
  private currentStats: MempoolStats = {
    size: 1,
    count: 100,
    feeHistogram: [],
    avgFeeRate: 1,
  };
  
  constructor(config: NebulaParticlesConfig) {
    super();
    
    this.config = config;
    this.maxParticles = config.maxParticles;
    this.userData = { id: 'nebula-particles', type: 'particles' };
    
    this.createParticleSystem();
    this.updateBehaviorFromStats();
  }
  
  private createParticleSystem(): void {
    // Create buffer arrays
    this.positions = new Float32Array(this.maxParticles * 3);
    this.colors = new Float32Array(this.maxParticles * 3);
    this.sizes = new Float32Array(this.maxParticles);
    this.lifetimes = new Float32Array(this.maxParticles);
    this.feeRates = new Float32Array(this.maxParticles);
    
    // Create geometry
    this.particleGeometry = new THREE.BufferGeometry();
    this.particleGeometry.setAttribute('position', new THREE.BufferAttribute(this.positions, 3));
    this.particleGeometry.setAttribute('color', new THREE.BufferAttribute(this.colors, 3));
    this.particleGeometry.setAttribute('size', new THREE.BufferAttribute(this.sizes, 1));
    this.particleGeometry.setAttribute('lifetime', new THREE.BufferAttribute(this.lifetimes, 1));
    this.particleGeometry.setAttribute('feeRate', new THREE.BufferAttribute(this.feeRates, 1));
    
    // Create shader material
    this.createParticleMaterial();
    
    // Create points system
    this.particleSystem = new THREE.Points(this.particleGeometry, this.particleMaterial);
    this.particleSystem.userData = { id: 'particle-system', type: 'points' };
    
    this.add(this.particleSystem);
  }
  
  private createParticleMaterial(): void {
    const vertexShader = `
      attribute float size;
      attribute float lifetime;
      attribute float feeRate;
      
      varying vec3 vColor;
      varying float vOpacity;
      varying float vSize;
      
      uniform float time;
      uniform float maxFeeRate;
      uniform sampler2D feeGradient;
      
      void main() {
        // Calculate size based on fee rate
        float sizeMultiplier = mix(0.5, 2.0, min(feeRate / maxFeeRate, 1.0));
        vSize = size * sizeMultiplier;
        
        // Color from fee gradient
        float feeT = min(feeRate / maxFeeRate, 1.0);
        vColor = texture2D(feeGradient, vec2(feeT, 0.5)).rgb;
        
        // Opacity based on lifetime (fade in/out)
        float normalizedLifetime = lifetime;
        vOpacity = smoothstep(0.0, 0.1, normalizedLifetime) * 
                   smoothstep(1.0, 0.8, normalizedLifetime);
        
        // Add subtle pulsing based on fee rate
        float pulse = 1.0 + sin(time * 3.0 + feeRate * 0.1) * 0.1;
        vSize *= pulse;
        
        // Transform position
        vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
        gl_Position = projectionMatrix * mvPosition;
        
        // Size attenuation with distance
        gl_PointSize = vSize * (300.0 / -mvPosition.z);
      }
    `;
    
    const fragmentShader = `
      varying vec3 vColor;
      varying float vOpacity;
      varying float vSize;
      
      uniform float time;
      
      void main() {
        // Create circular particle with soft edges
        vec2 center = gl_PointCoord - vec2(0.5);
        float dist = length(center);
        
        if (dist > 0.5) discard;
        
        // Soft circular falloff
        float alpha = 1.0 - smoothstep(0.2, 0.5, dist);
        
        // Add inner glow
        float innerGlow = 1.0 - smoothstep(0.0, 0.3, dist);
        vec3 finalColor = vColor + innerGlow * 0.3;
        
        // Subtle sparkle effect
        float sparkle = sin(time * 5.0 + gl_FragCoord.x * 0.1) * 
                       cos(time * 7.0 + gl_FragCoord.y * 0.1);
        finalColor += sparkle * 0.1;
        
        gl_FragColor = vec4(finalColor, alpha * vOpacity);
      }
    `;
    
    // Create fee gradient texture
    const feeGradientTexture = this.createFeeGradientTexture();
    
    this.particleMaterial = new THREE.ShaderMaterial({
      vertexShader,
      fragmentShader,
      uniforms: {
        time: { value: 0 },
        maxFeeRate: { value: 1000 },
        feeGradient: { value: feeGradientTexture },
      },
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthTest: true,
      depthWrite: false,
    });
  }
  
  private createFeeGradientTexture(): THREE.DataTexture {
    const width = 256;
    const data = new Uint8Array(width * 4);
    
    for (let i = 0; i < width; i++) {
      const t = i / (width - 1);
      let color: THREE.Color;
      
      // Fee gradient: blue (low) -> green (medium) -> orange (high) -> red (priority)
      if (t < 0.25) {
        color = new THREE.Color().lerpColors(
          new THREE.Color(0x4488ff), // Low fee blue
          new THREE.Color(0x44ff88), // Medium fee green
          t * 4
        );
      } else if (t < 0.75) {
        color = new THREE.Color().lerpColors(
          new THREE.Color(0x44ff88), // Medium fee green
          new THREE.Color(0xffaa44), // High fee orange
          (t - 0.25) * 2
        );
      } else {
        color = new THREE.Color().lerpColors(
          new THREE.Color(0xffaa44), // High fee orange
          new THREE.Color(0xff4444), // Priority fee red
          (t - 0.75) * 4
        );
      }
      
      const index = i * 4;
      data[index] = Math.floor(color.r * 255);
      data[index + 1] = Math.floor(color.g * 255);
      data[index + 2] = Math.floor(color.b * 255);
      data[index + 3] = 255;
    }
    
    const texture = new THREE.DataTexture(data, width, 1, THREE.RGBAFormat);
    texture.needsUpdate = true;
    return texture;
  }
  
  public spawn(transaction: Transaction): void {
    if (this.activeParticles.length >= this.maxParticles) {
      // Remove oldest particle to make room
      this.removeOldestParticle();
    }
    
    // Create new particle
    const particle: TransactionParticle = {
      id: transaction.id,
      position: this.getSpawnPosition(transaction.feeRate || 1),
      velocity: this.getInitialVelocity(transaction.feeRate || 1),
      lifetime: 1.0,
      maxLifetime: this.getParticleLifetime(transaction.feeRate || 1),
      feeRate: transaction.feeRate || 1,
      size: this.getParticleSize(transaction.feeRate || 1),
      color: this.getFeeColor(transaction.feeRate || 1),
      age: 0,
    };
    
    this.activeParticles.push(particle);
    this.updateParticleBuffers();
  }
  
  private getSpawnPosition(feeRate: number): THREE.Vector3 {
    const { bounds } = this.config;
    
    // Higher fee rates spawn closer to center
    const feeNormalized = Math.min(feeRate / 200, 1); // Normalize to 200 sat/vB max
    const radiusRange = bounds.outerRadius - bounds.innerRadius;
    const radius = bounds.outerRadius - (feeNormalized * radiusRange * 0.7);
    
    // Random angle
    const angle = Math.random() * Math.PI * 2;
    
    // Height varies with some randomness
    const height = (Math.random() - 0.5) * bounds.height * 0.8;
    
    return new THREE.Vector3(
      Math.cos(angle) * radius,
      height,
      Math.sin(angle) * radius
    );
  }
  
  private getInitialVelocity(feeRate: number): THREE.Vector3 {
    // Base swirl motion
    const swirlStrength = this.behavior.swirl * 2;
    const driftStrength = this.behavior.drift * 0.5;
    
    // Higher fee particles move faster
    const feeMultiplier = 1 + Math.min(feeRate / 100, 2);
    
    return new THREE.Vector3(
      (Math.random() - 0.5) * driftStrength * feeMultiplier,
      (Math.random() - 0.5) * driftStrength * 0.3,
      (Math.random() - 0.5) * driftStrength * feeMultiplier
    );
  }
  
  private getParticleLifetime(feeRate: number): number {
    // Higher fee particles have shorter lifetimes (faster processing)
    const baseDuration = 10; // seconds
    const feeSpeedup = Math.min(feeRate / 50, 5);
    return baseDuration / (1 + feeSpeedup * 0.5);
  }
  
  private getParticleSize(feeRate: number): number {
    // Size based on fee rate, clamped to reasonable range
    const baseSize = 0.5;
    const feeMultiplier = 1 + Math.min(feeRate / 100, 3);
    return baseSize * feeMultiplier;
  }
  
  private getFeeColor(feeRate: number): THREE.Color {
    const t = Math.min(feeRate / 200, 1);
    
    if (t < 0.25) {
      return new THREE.Color().lerpColors(
        new THREE.Color(0x4488ff),
        new THREE.Color(0x44ff88),
        t * 4
      );
    } else if (t < 0.75) {
      return new THREE.Color().lerpColors(
        new THREE.Color(0x44ff88),
        new THREE.Color(0xffaa44),
        (t - 0.25) * 2
      );
    } else {
      return new THREE.Color().lerpColors(
        new THREE.Color(0xffaa44),
        new THREE.Color(0xff4444),
        (t - 0.75) * 4
      );
    }
  }
  
  private removeOldestParticle(): void {
    if (this.activeParticles.length === 0) return;
    
    // Find particle with lowest lifetime
    let oldestIndex = 0;
    let oldestLifetime = this.activeParticles[0].lifetime;
    
    for (let i = 1; i < this.activeParticles.length; i++) {
      if (this.activeParticles[i].lifetime < oldestLifetime) {
        oldestIndex = i;
        oldestLifetime = this.activeParticles[i].lifetime;
      }
    }
    
    this.activeParticles.splice(oldestIndex, 1);
  }
  
  public update(deltaTime: number): void {
    this.time += deltaTime;
    
    // Update particle material time
    this.particleMaterial.uniforms['time'].value = this.time;
    
    // Update particle physics
    this.updateParticlePhysics(deltaTime);
    
    // Remove expired particles
    this.removeExpiredParticles();
    
    // Update GPU buffers
    this.updateParticleBuffers();
    
    // Spawn new particles based on mempool activity
    this.updateSpawning(deltaTime);
  }
  
  private updateParticlePhysics(deltaTime: number): void {
    const { bounds } = this.config;
    
    for (const particle of this.activeParticles) {
      // Age the particle
      particle.age += deltaTime;
      particle.lifetime = Math.max(0, 1 - (particle.age / particle.maxLifetime));
      
      // Apply swirl motion
      const centerDistance = Math.sqrt(
        particle.position.x * particle.position.x + 
        particle.position.z * particle.position.z
      );
      
      if (centerDistance > 0.1) {
        const swirlForce = this.behavior.swirl * deltaTime;
        const angle = Math.atan2(particle.position.z, particle.position.x);
        const newAngle = angle + swirlForce / centerDistance;
        
        const swirlVelocity = new THREE.Vector3(
          Math.cos(newAngle) * centerDistance - particle.position.x,
          0,
          Math.sin(newAngle) * centerDistance - particle.position.z
        ).multiplyScalar(2);
        
        particle.velocity.add(swirlVelocity.multiplyScalar(deltaTime));
      }
      
      // Apply outward drift (transactions leaving mempool)
      const driftDirection = particle.position.clone().normalize();
      const driftForce = driftDirection.multiplyScalar(this.behavior.drift * deltaTime);
      particle.velocity.add(driftForce);
      
      // Apply turbulence
      const turbulence = new THREE.Vector3(
        (Math.random() - 0.5) * this.behavior.turbulence,
        (Math.random() - 0.5) * this.behavior.turbulence * 0.3,
        (Math.random() - 0.5) * this.behavior.turbulence
      ).multiplyScalar(deltaTime);
      particle.velocity.add(turbulence);
      
      // Apply velocity damping
      particle.velocity.multiplyScalar(0.98);
      
      // Update position
      particle.position.add(particle.velocity.clone().multiplyScalar(deltaTime));
      
      // Boundary checking
      const distance = particle.position.length();
      if (distance > bounds.outerRadius * 1.5) {
        // Particle has left the mempool - mark for removal
        particle.lifetime = 0;
      }
    }
  }
  
  private removeExpiredParticles(): void {
    this.activeParticles = this.activeParticles.filter(particle => particle.lifetime > 0);
  }
  
  private updateParticleBuffers(): void {
    const maxParticles = this.maxParticles;
    
    // Reset all buffers
    this.positions.fill(0);
    this.colors.fill(0);
    this.sizes.fill(0);
    this.lifetimes.fill(0);
    this.feeRates.fill(0);
    
    // Fill buffers with active particles
    for (let i = 0; i < Math.min(this.activeParticles.length, maxParticles); i++) {
      const particle = this.activeParticles[i];
      
      // Position
      const posIndex = i * 3;
      this.positions[posIndex] = particle.position.x;
      this.positions[posIndex + 1] = particle.position.y;
      this.positions[posIndex + 2] = particle.position.z;
      
      // Color
      const colorIndex = i * 3;
      this.colors[colorIndex] = particle.color.r;
      this.colors[colorIndex + 1] = particle.color.g;
      this.colors[colorIndex + 2] = particle.color.b;
      
      // Size
      this.sizes[i] = particle.size;
      
      // Lifetime
      this.lifetimes[i] = particle.lifetime;
      
      // Fee rate
      this.feeRates[i] = particle.feeRate;
    }
    
    // Update buffer attributes
    this.particleGeometry.attributes['position'].needsUpdate = true;
    this.particleGeometry.attributes['color'].needsUpdate = true;
    this.particleGeometry.attributes['size'].needsUpdate = true;
    this.particleGeometry.attributes['lifetime'].needsUpdate = true;
    this.particleGeometry.attributes['feeRate'].needsUpdate = true;
    
    // Update draw range
    this.particleGeometry.setDrawRange(0, Math.min(this.activeParticles.length, maxParticles));
  }
  
  private updateSpawning(_deltaTime: number): void {
    // Spawn particles based on mempool activity
    const timeSinceLastSpawn = this.time - this.lastSpawn;
    const spawnInterval = 1.0 / this.spawnRate;
    
    if (timeSinceLastSpawn >= spawnInterval) {
      // Create mock transaction for ambient spawning
      const feeRate = this.generateRandomFeeRate();
      const size = Math.floor(Math.random() * 500 + 250);
      const mockTransaction: Transaction = {
        id: `mock_${this.time}_${Math.random()}`,
        amount: Math.random() * 1000000,
        fee: Math.floor(feeRate * size),
        feeRate,
        size,
        timestamp: Date.now(),
        inputs: [],
        outputs: [],
        status: 'pending',
      };
      
      this.spawn(mockTransaction);
      this.lastSpawn = this.time;
    }
  }
  
  private generateRandomFeeRate(): number {
    // Generate fee rate based on current mempool distribution
    const rand = Math.random();
    
    if (rand < 0.4) return Math.random() * 10; // Low fees
    if (rand < 0.7) return 10 + Math.random() * 40; // Medium fees
    if (rand < 0.9) return 50 + Math.random() * 150; // High fees
    return 200 + Math.random() * 300; // Priority fees
  }
  
  private updateBehaviorFromStats(): void {
    // Adjust particle behavior based on mempool congestion
    const congestionFactor = Math.min(this.currentStats.size / 100, 1);
    
    this.behavior.turbulence = 0.2 + congestionFactor * 0.5;
    this.behavior.swirl = 0.5 + congestionFactor * 0.3;
    this.spawnRate = 5 + congestionFactor * 10;
  }
  
  public updateMempoolStats(stats: MempoolStats): void {
    this.currentStats = stats;
    this.updateBehaviorFromStats();
    
    // Update shader uniforms
    this.particleMaterial.uniforms['maxFeeRate'].value = Math.max(stats.avgFeeRate * 5, 100);
  }
  
  public setMaxParticles(count: number): void {
    this.maxParticles = Math.max(1, Math.min(count, this.config.maxParticles));
  }
  
  public setQualityLevel(level: 'low' | 'medium' | 'high' | 'ultra'): void {
    this.config.qualityLevel = level;
    
    // Adjust particle count based on quality
    const qualityMultipliers = {
      low: 0.3,
      medium: 0.6,
      high: 1.0,
      ultra: 1.5,
    };
    
    this.setMaxParticles(Math.floor(this.config.maxParticles * qualityMultipliers[level]));
  }
  
  public getActiveParticleCount(): number {
    return this.activeParticles.length;
  }
  
  public convertToShip(particleId: string): TransactionParticle | null {
    const index = this.activeParticles.findIndex(p => p.id === particleId);
    if (index === -1) return null;
    
    const particle = this.activeParticles[index];
    this.activeParticles.splice(index, 1);
    
    return particle;
  }
  
  public dispose(): void {
    this.particleGeometry.dispose();
    this.particleMaterial.dispose();
    
    // Clear particle arrays
    this.activeParticles = [];
  }
}